/* <pdp-shape-finder>: product page 'shape_finder' block. Runs the quiz inside the block's native <dialog>: opens it from
   the trigger (or from the editor when the block or one of its child blocks is selected), locks the page scroll through
   OrdoUtils the way the drawers do, walks the [data-sf-step] sections, validates each question, fetches the picked
   colour's products from sections/pdp-shape-finder-data.liquid (Liquid-rendered variant data and images, kept in a
   detached document until the summary needs one), resolves the variant from the shape + texture + size answers, fills
   the summary from that data and adds the variant through window.handleAddToCart (cart drawer). The dialog is closed
   before the cart call because the top layer would sit above the drawer. When the page also holds an Email button
   block (<pdp-email-button>), the summary shows a mailto: link built from that block's settings instead of Add to cart.
   Everything else is looked up inside this element except the cart drawer. */
if (!customElements.get('pdp-shape-finder')) {
  class PdpShapeFinder extends HTMLElement {
    static get KEYS() { return ['shape', 'texture', 'size']; }
    static get KINDS() { return ['bundle', 'handle']; }
    static get SECTION() { return 'pdp-shape-finder-data'; }

    static norm(value) {
      return String(value == null ? '' : value).trim().toLowerCase();
    }

    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-sf-open]');
      if (!this.dialog || !this.trigger) return;
      this.steps = Array.from(this.querySelectorAll('[data-sf-step]'));
      this.questions = this.steps.filter((step) => step.hasAttribute('data-sf-question'));
      this.nav = this.querySelector('[data-sf-nav]');
      this.error = this.querySelector('[data-sf-error]');
      this.progress = this.querySelector('[data-sf-progress]');
      this.scroller = this.querySelector('[data-sf-scroller]');
      this.index = 0;
      this._locked = false;
      this._store = new Map();
      this._summaryToken = 0;
      this._ac = new AbortController();
      const { signal } = this._ac;

      this.trigger.addEventListener('click', () => this.open(), { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) { this.close(); return; }
        const control = event.target.closest('[data-sf-close], [data-sf-next], [data-sf-back], [data-sf-add]');
        if (!control || !this.dialog.contains(control)) return;
        if (control.hasAttribute('data-sf-close')) this.close();
        else if (control.hasAttribute('data-sf-next')) this.next();
        else if (control.hasAttribute('data-sf-back')) this.back();
        else if (control.hasAttribute('data-sf-add')) this.addToCart(control, event);
      }, { signal });
      this.dialog.addEventListener('close', () => {
        this._unlock();
        this.trigger.focus({ preventScroll: true });
      }, { signal });
      this.dialog.addEventListener('change', (event) => this._onChange(event), { signal });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (!this.contains(event.target)) return;
          const step = event.target.closest('[data-sf-step]');
          this.open(step ? step.dataset.sfStep : this.dataset.previewStep);
        }, { signal });
        document.addEventListener('shopify:block:deselect', (event) => {
          if (this.contains(event.target)) this.close();
        }, { signal });
      }
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      this._unlock();
    }

    open(stepName) {
      const index = Math.max(0, this.steps.findIndex((step) => step.dataset.sfStep === stepName));
      if (!this.dialog.open) {
        this._lock();
        this.dialog.showModal();
        const colour = this._value('colour');
        if (colour) this._prefetch(colour);
      }
      this.goTo(index);
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    _lock() {
      if (this._locked) return;
      this._locked = true;
      OrdoUtils.lockScroll('modal-opened');
    }

    _unlock() {
      if (!this._locked) return;
      this._locked = false;
      OrdoUtils.unlockScroll('modal-opened');
    }

    goTo(index) {
      this.index = Math.max(0, Math.min(index, this.steps.length - 1));
      const step = this.steps[this.index];
      if (!step) return;
      this.steps.forEach((section, n) => { section.hidden = n !== this.index; });
      const isQuestion = step.hasAttribute('data-sf-question');
      if (this.nav) this.nav.hidden = !isQuestion;
      this._hideError();
      if (this.progress) {
        this.progress.hidden = !isQuestion;
        if (isQuestion) {
          this.progress.textContent = (this.dataset.labelStep || '')
            .replace('{{ index }}', String(this.questions.indexOf(step) + 1))
            .replace('{{ total }}', String(this.questions.length));
        }
      }
      const name = step.dataset.sfStep;
      if (name === 'bundle') this._syncBundle();
      if (name === 'summary') this._renderSummary();
      const title = step.querySelector('[data-sf-title]');
      if (title) {
        if (title.id) this.dialog.setAttribute('aria-labelledby', title.id);
        title.focus({ preventScroll: true });
      }
      if (this.scroller) this.scroller.scrollTop = 0;
      // Warm the next step's option images.
      const next = this.steps[this.index + 1];
      if (next) next.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }

    next() {
      const step = this.steps[this.index];
      if (step && step.hasAttribute('data-sf-question') && !step.querySelector('input:checked')) {
        this._showError(step);
        return;
      }
      this.goTo(this.index + 1);
    }

    back() {
      this.goTo(this.index - 1);
    }

    _showError(step) {
      if (this.error) this.error.hidden = false;
      const first = step.querySelector('input:not(:disabled)');
      if (first) first.focus({ preventScroll: true });
    }

    _hideError() {
      if (this.error) this.error.hidden = true;
    }

    _onChange(event) {
      const input = event.target;
      if (!input || input.tagName !== 'INPUT') return;
      this._hideError();
      if (input.closest('[data-sf-step="colour"]')) this._prefetch(input.value);
      const group = input.closest('[data-sf-max]');
      if (!group) return;
      const max = parseInt(group.dataset.sfMax, 10) || 0;
      const boxes = Array.from(group.querySelectorAll('input[type="checkbox"]'));
      const count = boxes.filter((box) => box.checked).length;
      boxes.forEach((box) => { if (!box.checked) box.disabled = max > 0 && count >= max; });
    }

    _input(stepName) {
      return this.querySelector('[data-sf-step="' + stepName + '"] input:checked');
    }

    _value(stepName) {
      const input = this._input(stepName);
      return input ? input.value : '';
    }

    // The colour block carries one product URL per kind; a kind without a URL is not sold in that colour.
    _url(colour, kind) {
      if (!colour || !kind) return '';
      const wrap = this.querySelector('[data-sf-colour="' + CSS.escape(colour) + '"]');
      return wrap ? wrap.getAttribute('data-sf-' + kind + '-url') || '' : '';
    }

    _prefetch(colour) {
      PdpShapeFinder.KINDS.forEach((kind) => this._load(colour, kind));
    }

    // Fetches the product's data section once and keeps the detached fragment; a failed request is retried next time.
    _load(colour, kind) {
      const url = this._url(colour, kind);
      if (!url) return Promise.resolve(null);
      const key = colour + '|' + kind;
      if (!this._store.has(key)) {
        const joiner = url.includes('?') ? '&' : '?';
        const request = fetch(url + joiner + 'section_id=' + PdpShapeFinder.SECTION)
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
          .then((html) => OrdoUtils.parseHTML(html).querySelector('[data-sf-product]') || null)
          .catch(() => {
            this._store.delete(key);
            return null;
          });
        this._store.set(key, request);
      }
      return this._store.get(key);
    }

    _syncBundle() {
      const colour = this._value('colour');
      const inputs = Array.from(this.querySelectorAll('[data-sf-step="bundle"] input'));
      const available = inputs.filter((input) => {
        const ok = !!this._url(colour, input.value);
        input.disabled = !ok;
        if (!ok && input.checked) input.checked = false;
        return ok;
      });
      if (available.length === 1 && !available[0].checked) available[0].checked = true;
      const note = this.querySelector('[data-sf-bundle-unavailable]');
      if (note) note.hidden = available.length > 0;
    }

    _data(fragment) {
      if (fragment._sfData !== undefined) return fragment._sfData;
      let data = null;
      try {
        const script = fragment.querySelector('[data-sf-variants]');
        data = script ? JSON.parse(script.textContent) : null;
      } catch (error) {
        data = null;
      }
      if (data && Array.isArray(data.o) && Array.isArray(data.v)) {
        const names = data.o.map((name) => PdpShapeFinder.norm(name));
        data.index = {};
        PdpShapeFinder.KEYS.forEach((key) => { data.index[key] = names.indexOf(key); });
      } else {
        data = null;
      }
      fragment._sfData = data;
      return data;
    }

    // A product missing one of the three options never matches; the colour block's editor note says which.
    _match(data, answers) {
      if (PdpShapeFinder.KEYS.some((key) => data.index[key] < 0)) return null;
      return data.v.find((row) => Array.isArray(row[1]) && PdpShapeFinder.KEYS.every((key) => (
        PdpShapeFinder.norm(row[1][data.index[key]]) === PdpShapeFinder.norm(answers[key])
      ))) || null;
    }

    async _renderSummary() {
      const token = ++this._summaryToken;
      const answers = {};
      PdpShapeFinder.KEYS.forEach((key) => { answers[key] = this._value(key); });
      const colourInput = this._input('colour');
      const bundleInput = this._input('bundle');
      const setText = (selector, text) => {
        const el = this.querySelector(selector);
        if (el) el.textContent = text || '';
      };
      PdpShapeFinder.KEYS.forEach((key) => setText('[data-sf-dd="' + key + '"]', answers[key]));
      setText('[data-sf-dd="colour"]', colourInput ? colourInput.dataset.sfLabel : '');
      setText('[data-sf-dd="bundle"]', bundleInput ? bundleInput.dataset.sfLabel : '');

      const body = this.querySelector('[data-sf-summary-body]');
      const slot = this.querySelector('[data-sf-summary-media]');
      const add = this.querySelector('[data-sf-add]');
      const addText = this.querySelector('[data-sf-add-text]');
      const unavailable = this.querySelector('[data-sf-unavailable]');
      const email = this.querySelector('[data-sf-email]');
      if (slot) { slot.replaceChildren(); slot.hidden = true; }
      if (add) { add.hidden = true; add.disabled = true; delete add.dataset.sfVariant; }
      if (email) email.hidden = true;
      if (unavailable) unavailable.hidden = true;
      if (body) body.setAttribute('aria-busy', 'true');

      const fragment = await this._load(colourInput ? colourInput.value : '', bundleInput ? bundleInput.value : '');
      if (token !== this._summaryToken) return;
      if (body) body.removeAttribute('aria-busy');
      const data = fragment ? this._data(fragment) : null;
      const row = data ? this._match(data, answers) : null;
      if (row) PdpShapeFinder.KEYS.forEach((key) => setText('[data-sf-dd="' + key + '"]', row[1][data.index[key]]));

      if (slot && fragment) {
        const mediaId = row && row[4] ? String(row[4]) : '';
        const image = (mediaId && fragment.querySelector('[data-sf-media="' + CSS.escape(mediaId) + '"]')) || fragment.querySelector('[data-sf-media="featured"]');
        if (image) {
          const clone = image.cloneNode(true);
          clone.removeAttribute('data-sf-media');
          slot.append(clone);
          slot.hidden = false;
        }
      }

      // Email mode also covers sold-out variants and combinations without one, so those customers can still ask.
      const source = email ? this._emailSource() : null;
      if (source) {
        const label = source.querySelector('.button__text');
        setText('[data-sf-email-text]', label ? label.textContent.trim() : '');
        email.href = this._mailto(source, data && data.t);
        email.hidden = false;
      }

      if (!row) {
        if (unavailable) unavailable.hidden = false;
        return;
      }
      if (!add || source) return;
      const available = !!row[2];
      add.hidden = false;
      add.disabled = !available;
      add.dataset.sfVariant = String(row[0]);
      if (addText) {
        addText.textContent = available ? (add.dataset.labelAdd || '') : (add.dataset.labelSoldOut || '');
        if (available && row[3]) {
          const price = document.createElement('span');
          price.className = 'bold-atc-price';
          price.textContent = ' - ' + row[3];
          addText.append(price);
        }
      }
    }

    // Automatic: an Email button block on the page replaces the summary's Add to cart; one in the quiz's own section wins.
    _emailSource() {
      const selector = 'pdp-email-button[data-email]';
      const scope = this.closest('pdp2-section') || this.closest('.shopify-section');
      return (scope && scope.querySelector(selector)) || document.querySelector(selector);
    }

    // Same format as the Email button block: message, blank line, product name, then the summary rows as shown.
    _mailto(source, title) {
      const settings = source.dataset;
      const lines = [];
      if (settings.message) lines.push(settings.message.replace(/\r?\n/g, '\r\n'), '');
      if (title) lines.push((settings.labelProduct || 'Product') + ': ' + title);
      this.querySelectorAll('[data-sf-dd]').forEach((dd) => {
        const dt = dd.previousElementSibling;
        const value = dd.textContent.trim();
        if (dt && value) lines.push(dt.textContent.trim() + ': ' + value);
      });
      const subject = settings.subject || title || '';
      return 'mailto:' + settings.email + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(lines.join('\r\n'));
    }

    addToCart(button, event) {
      const id = button.dataset.sfVariant;
      if (!id || button.disabled) return;
      const viaKeyboard = !!event && event.detail === 0;
      this.trigger.classList.add('working');
      // Unlock before the drawer opens so it saves the real scroll offset, then close (the close event finds nothing to undo).
      this._unlock();
      this.close();
      const body = JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] });
      window.handleAddToCart(body, 1, { 'Content-Type': 'application/json' }, () => {
        this.trigger.classList.remove('working');
        if (viaKeyboard) OrdoUtils.focusCartClose();
      });
    }
  }

  customElements.define('pdp-shape-finder', PdpShapeFinder);
}
